/**
 * Blog.tsx — /blog
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Clean grid layout, generous whitespace, editorial card style
 * - Muted earth tones, Playfair Display headings, Source Sans 3 body
 * - Featured article large, rest in grid
 */

import { useState } from "react";
import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { Clock, ChevronRight, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const ARTICLE1_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article1-v1-mBURY4muT8nNAjUnDwQRbz.webp";
const ARTICLE2_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/hero-article2-v1-ZjmAs5X6RgtMKCGR8QmQdR.webp";
const ARTICLE3_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article3-residential-complex-Vstb4C43kK3pRH4WYGacpF.webp";
const ARTICLE4_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article4-market-hero_bed3bdbc.webp";
const ARTICLE5_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/article5-piata-sfatului-night_37c559b4.webp";
const ARTICLE6_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/uLiraOubpeeGNSzh.webp";
const ARTICLE7_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/lYOlLhyWGyRBhTWk.webp";
const ARTICLE8_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/cbxBOxJCiaYOARrK.webp";

const ARTICLE9_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/SIuiZFHgwspEQwFc.webp";
const ARTICLE10_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/tqdGOmhsJOvWFMNk.webp";
const ARTICLE11_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/JrhUEHsfckCWpGfz.webp";
const ARTICLE12_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/kYQbBouhSHqeCLzh.webp";
const ARTICLE13_IMG = "https://files.manuscdn.com/user_upload_by_module/session_file/310419663030265086/NXjgILHSTXidEaJP.webp";

const ARTICLES = [
  {
    id: 13,
    href: "/working-in-romania-foreigner-guide",
    category: "Work & Immigration",
    title: "Working in Romania as a Foreigner: The 2026 Law Explained",
    excerpt: "OUG 32/2026 replaced the entire framework for hiring non-EU workers in Romania. Two new visa types, a national platform, and explicit worker rights. Here is what it actually means if you want to work here legally.",
    img: ARTICLE13_IMG,
    readTime: "12 min read",
    date: "April 29, 2026",
    featured: true,
  },
  {
    id: 12,
    href: "/brasov-bus-guide",
    category: "City Guide",
    title: "Bus No. 24 Brașov & 4 More Routes Worth Taking — Public Transport Guide",
    excerpt: "A single bus ticket costs 5 lei. Some of the routes take you places no walking tour will. Linia 24 along Strada Lungă, Linia 20 to Poiana Brașov, Linia 55 to Cetățuia — and two more routes most visitors never hear about.",
    img: ARTICLE12_IMG,
    readTime: "8 min read",
    date: "April 28, 2026",
    featured: false,
  },
  {
    id: 11,
    href: "/black-church-brasov-visitor-guide",
    category: "City Guide",
    title: "The Black Church in Brașov: what it actually looks like inside, and how to plan the visit",
    excerpt: "The largest Gothic church in Romania, a carpet collection that traces six centuries of Saxon trade, and an organ that fills the nave twice a week. Everything you need to visit the Black Church — tickets, hours, and what to see.",
    img: ARTICLE11_IMG,
    readTime: "7 min read",
    date: "April 22, 2026",
    featured: false,
  },
  {
    id: 10,
    href: "/buy-property-romania-auctions-seized-assets",
    category: "Real Estate",
    title: "How to buy property below market value in Romania (2026)",
    excerpt: "Romania's new eLicitațiiANAF platform lists seized cars, apartments, land, and industrial equipment at prices that can go well below market. Here is how foreign investors can access it, what the process actually looks like, and what risks nobody mentions.",
    img: ARTICLE10_IMG,
    readTime: "8 min read",
    date: "April 21, 2026",
    featured: true,
  },
  {
    id: 9,
    href: "/buying-apartment-foreigner-brasov",
    category: "Real Estate",
    title: "How to Buy an Apartment in Brașov as a Foreigner: The 2026 Guide",
    excerpt: "From getting your Romanian NIF at ANAF to navigating the new Nordis Law protections for off-plan purchases. Everything a foreign buyer needs to know about the legal process, required documents, and closing costs in Brașov.",
    img: ARTICLE9_IMG,
    readTime: "9 min read",
    date: "April 15, 2026",
    featured: false,
  },
  {
    id: 8,
    href: "/company-setup-romania",
    category: "Business",
    title: "Company setup in Romania in 2026: the complete guide for expats and foreign investors",
    excerpt: "Romania's 1% micro-enterprise tax is still the most competitive rate in the EU — but the rules have changed. Here is what it actually takes to open and run an SRL in 2026, including how Romania compares to Bulgaria, Estonia, Germany, and the Netherlands.",
    img: ARTICLE8_IMG,
    readTime: "10 min read",
    date: "April 14, 2026",
    featured: false,
  },
  {
    id: 7,
    href: "/brasov-vs-budapest-prague-vienna-expats",
    category: "Relocation",
    title: "Brașov vs Budapest, Prague & Vienna: Why Expats Are Choosing Romania",
    excerpt: "A data-driven comparison of Brașov against three of Central Europe's most popular expat destinations — cost of living, rent, safety, and what your money actually buys in each city.",
    img: ARTICLE7_IMG,
    readTime: "12 min read",
    date: "April 8, 2026",
    featured: false,
  },
  {
    id: 6,
    href: "/cartier-transilvania-brasov",
    category: "Real Estate",
    title: "Buying an Apartment in Brașov in 2026: What to Look For, and One Development Worth Knowing",
    excerpt: "The Brașov apartment market is still moving. But in a city where new developments multiply rapidly, the quality gap between developers has become the single most important variable in the buying decision.",
    img: ARTICLE6_IMG,
    readTime: "8 min read",
    date: "April 5, 2026",
    featured: false,
  },
  {
    id: 5,
    href: "/entertainment-brasov-guide",
    category: "Lifestyle",
    title: "Entertainment in Brașov: Day & Night for Adults — with Real Prices",
    excerpt: "From rooftop cocktail bars and escape rooms to opera and treetop adventure parks — a complete guide to what Brașov actually offers adults, with prices adjusted to reflect what you will really pay.",
    img: ARTICLE5_IMG,
    readTime: "9 min read",
    date: "April 3, 2026",
    featured: false,
  },
  {
    id: 4,
    href: "/cost-of-living-brasov",
    category: "Relocation",
    title: "Cost of Living in Brașov 2026: the honest numbers, neighbourhood by neighbourhood",
    excerpt: "Rents have roughly doubled in six years, property prices hit record highs in 2025, and grocery inflation has been persistent. Here are the actual numbers — no tourist-brochure spin.",
    img: ARTICLE4_IMG,
    readTime: "10 min read",
    date: "April 3, 2026",
    featured: false,
  },
  {
    id: 1,
    href: "/why-expats-choose-brasov",
    category: "City Guide",
    title: "Brașov, straight up: beyond the bear photos and the mountain air clichés",
    excerpt: "If you are thinking about moving your life and your money here, you have probably already heard the tired line about the city at the foot of Tampa Mountain. Skip the poetry.",
    img: ARTICLE1_IMG,
    readTime: "7 min read",
    date: "April 1, 2026",
    featured: false,
  },
  {
    id: 2,
    href: "/what-to-do-in-brasov-city-guide",
    category: "City Guide",
    title: "What to do in Brașov: a guide for people who are tired of tourist guides",
    excerpt: "The old town is small enough to walk in an hour, which means most people do exactly that — and miss everything worth finding.",
    img: ARTICLE2_IMG,
    readTime: "8 min read",
    date: "April 1, 2026",
    featured: false,
  },
  {
    id: 3,
    href: "/buying-apartment-brasov",
    category: "Real Estate",
    title: "Brașov real estate: a decade of prices that kept going up — and why they're not done yet",
    excerpt: "In roughly ten years, prices in Brașov have nearly tripled. The question worth asking is whether that curve is still going up, or whether you've already missed the train.",
    img: ARTICLE3_IMG,
    readTime: "9 min read",
    date: "April 1, 2026",
    featured: false,
  },
];

const CATEGORIES = ["All", "City Guide", "Real Estate", "Relocation", "Lifestyle", "Business"];

export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredArticles = activeCategory === "All"
    ? ARTICLES
    : ARTICLES.filter((a) => a.category === activeCategory);

  const featured = filteredArticles.find((a) => a.featured) ?? filteredArticles[0];
  const rest = filteredArticles.filter((a) => a !== featured);

  return (
    <>
    <Helmet>
      <title>Brașov Gateway Blog — Guides, Tips & Real Estate Insights</title>
      <meta name="description" content="Practical guides, real estate insights, and local knowledge for foreigners moving to Brașov, Romania. Written by locals, verified by expats." />
      <meta property="og:title" content="Brașov Gateway Blog — Guides, Tips & Real Estate Insights" />
      <meta property="og:description" content="Practical guides, real estate insights, and local knowledge for foreigners moving to Brașov, Romania. Written by locals, verified by expats." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/home-hero-brasov-v2_fd2b2f8b.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://brasovgateway.com/blog" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Brașov Gateway Blog — Guides, Tips & Real Estate Insights" />
      <link rel="canonical" href="https://brasovgateway.com/blog" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Blog",
          "@id": "https://brasovgateway.com/blog",
          "name": "Brașov Gateway Blog",
          "description": "Practical guides, real estate insights, and local knowledge for foreigners moving to Brașov, Romania.",
          "url": "https://brasovgateway.com/blog",
          "publisher": {
            "@type": "Organization",
            "name": "Brașov Gateway",
            "url": "https://brasovgateway.com"
          },
          "inLanguage": "en"
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen" style={{ fontFamily: "'Source Sans 3', sans-serif", backgroundColor: "oklch(0.985 0.006 75)" }}>
      <SiteHeader />

      {/* Page Header */}
      <section className="pt-32 pb-12 px-4 bg-white border-b border-stone-100">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.2em] mb-4" style={{ color: "oklch(0.38 0.12 15)" }}>
            From the blog
          </p>
          <h1 className="text-5xl md:text-6xl text-stone-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Guides & Insights
          </h1>
          <p className="text-lg text-stone-500 max-w-2xl leading-relaxed">
            Honest writing about living, investing, and doing business in Brașov — without the tourist-brochure spin.
          </p>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mt-8">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="text-xs uppercase tracking-widest px-4 py-2 border transition-colors"
                style={
                  cat === activeCategory
                    ? { backgroundColor: "oklch(0.38 0.12 15)", color: "white", borderColor: "oklch(0.38 0.12 15)" }
                    : { backgroundColor: "transparent", color: "oklch(0.45 0.01 60)", borderColor: "oklch(0.85 0.01 60)" }
                }
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {filteredArticles.length === 0 ? (
        <section className="py-24 px-4 bg-white">
          <div className="container max-w-5xl text-center">
            <p className="text-stone-400 text-sm uppercase tracking-widest mb-4">No articles yet</p>
            <h2 className="text-2xl text-stone-700" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nothing published in this category yet.
            </h2>
            <button
              onClick={() => setActiveCategory("All")}
              className="mt-8 text-xs uppercase tracking-widest px-6 py-3 text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: "oklch(0.38 0.12 15)" }}
            >
              View all articles
            </button>
          </div>
        </section>
      ) : (
      <section className="py-16 px-4 bg-white">
        <div className="container max-w-5xl">
          <Link href={featured.href} className="no-underline group block">
            <div className="grid md:grid-cols-2 gap-0 border border-stone-200 overflow-hidden hover:shadow-md transition-shadow">
              <div className="relative h-64 md:h-auto min-h-64 overflow-hidden">
                <img
                  src={featured.img}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to right, transparent 60%, white 100%)" }} />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                    {featured.category}
                  </span>
                  <span className="text-xs uppercase tracking-widest text-stone-400 px-2 py-1 border border-stone-200">
                    Featured
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl text-stone-900 mb-4 leading-tight group-hover:text-stone-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {featured.title}
                </h2>
                <p className="text-stone-500 text-sm leading-relaxed mb-6">
                  {featured.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-stone-400 text-xs">
                    <Clock size={11} />
                    <span>{featured.readTime}</span>
                    <span>·</span>
                    <span>{featured.date}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest group-hover:gap-3 transition-all" style={{ color: "oklch(0.38 0.12 15)" }}>
                    Read more <ArrowRight size={13} />
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>
      )}

      {/* Article Grid */}
      <section className="py-8 pb-24 px-4" style={{ backgroundColor: "oklch(0.985 0.006 75)" }}>
        <div className="container max-w-5xl">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl text-stone-700" style={{ fontFamily: "'Playfair Display', serif" }}>
              More articles
            </h2>
            <span className="text-xs text-stone-400 uppercase tracking-widest">{filteredArticles.length} article{filteredArticles.length !== 1 ? "s" : ""}</span>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {rest.map((article) => (
              <Link key={article.id} href={article.href} className="no-underline group block">
                <div className="bg-white border border-stone-200 overflow-hidden hover:shadow-md transition-shadow h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.img}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs uppercase tracking-widest font-semibold px-2 py-1" style={{ color: "oklch(0.38 0.12 15)", backgroundColor: "oklch(0.95 0.02 20)" }}>
                        {article.category}
                      </span>
                    </div>
                    <h3 className="text-lg text-stone-900 mb-3 leading-snug group-hover:text-stone-700 transition-colors flex-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {article.title}
                    </h3>
                    <p className="text-stone-500 text-sm leading-relaxed mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-stone-100">
                      <div className="flex items-center gap-2 text-stone-400 text-xs">
                        <Clock size={11} />
                        <span>{article.readTime}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest group-hover:gap-2 transition-all" style={{ color: "oklch(0.38 0.12 15)" }}>
                        Read <ChevronRight size={12} />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 text-white" style={{ backgroundColor: "oklch(0.18 0.01 60)" }}>
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.2em] mb-4 opacity-60">Ready to take the next step?</p>
          <h2 className="text-3xl md:text-4xl mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Reading is a start.<br />
            <em className="not-italic" style={{ color: "oklch(0.75 0.1 40)" }}>A conversation is better.</em>
          </h2>
          <p className="text-stone-400 mb-10 max-w-xl mx-auto leading-relaxed">
            If something you read here sparked a question, I'm happy to answer it — no commitment, no sales pitch.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 text-white no-underline text-xs uppercase tracking-widest hover:opacity-90 transition-opacity"
            style={{ backgroundColor: "oklch(0.38 0.12 15)" }}
          >
            Get in touch <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
    </>
  );
}
